import { ReturnFrequency } from 'store/api/enums';
import { TaxRateType } from 'store/app/enums';

export type MappingTaxRate = {
  taxCodeName?: string;
  taxRate?: string;
  taxType?: string;
  codes?: string;
  reverseCharge: string | null;
  taxRateType: TaxRateType;
};

/** Account-to-box row stringified into jsonMapping for a Malta TIFD save. */
export type TifdAccountMapping = {
  accountId: string;
  accountCode: string;
  accountName: string;
  accountType: string;
  tifdCode: string;
  creditBalanceTifdCode: string | null;
  activityNo: number | null;
  mappedBy: string;
};

/** Income-sheet activity stored beside the account rows in a Malta TIFD save. */
export type TifdActivity = {
  activityNo: number;
  establishmentName: string;
  operationDescription: string;
};

/** Editable activity row. activityNo is assigned from the row order on save. */
export type TifdActivityDraft = {
  key: string;
  establishmentName: string;
  operationDescription: string;
};

export const TIFD_ACTIVITY_LIMIT = 5;
export const TIFD_ACTIVITY_FIELD_MAX = 39;

export const createTifdActivityDraft = (
  establishmentName = '',
  operationDescription = ''
): TifdActivityDraft => ({
  key: `activity-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  establishmentName: establishmentName.slice(0, TIFD_ACTIVITY_FIELD_MAX),
  operationDescription: operationDescription.slice(0, TIFD_ACTIVITY_FIELD_MAX),
});

/** Latest activity rows, written on each keystroke so save does not read a stale copy. */
const TIFD_ACTIVITY_DRAFTS_KEY = '__generateTaxTifdActivityDrafts';

const activityDraftStore = globalThis as typeof globalThis & {
  [TIFD_ACTIVITY_DRAFTS_KEY]?: TifdActivityDraft[];
};

export const writeTifdActivityDrafts = (next: TifdActivityDraft[]) => {
  activityDraftStore[TIFD_ACTIVITY_DRAFTS_KEY] = next;
};

export const readTifdActivityDrafts = (): TifdActivityDraft[] =>
  activityDraftStore[TIFD_ACTIVITY_DRAFTS_KEY] ?? [];

export const tifdActivityText = (value?: string) => (value ?? '').trim();

export const tifdActivitySaveBlock = (
  rows: TifdActivityDraft[]
): 'required' | 'empty' | null => {
  const complete = rows.filter(
    (row) =>
      tifdActivityText(row.establishmentName) &&
      tifdActivityText(row.operationDescription)
  );
  if (complete.length === 0) return 'required';
  if (
    rows.some(
      (row) =>
        !tifdActivityText(row.establishmentName) ||
        !tifdActivityText(row.operationDescription)
    )
  ) {
    return 'empty';
  }
  return null;
};

export type MappingData = {
  mappingId?: string;
  templateMappingId?: string;
  typeId?: string;
  /** Display name of the selected return type. Kept so the mapping header does not depend on a later list lookup. */
  typeName?: string;
  /** Country label for the selected return type, shown beside the type name. */
  countryName?: string;
  /** Selected OSS section IDs from ChooseReturn drawer. */
  ossCountryIds?: string[];
  mappedCashAccounting?: boolean;
  mappedReverseCharges?: boolean;
  periodFromDate?: string;
  returnFrequency?: ReturnFrequency;
  returnDueDate?: string;
  jsonMapping?: MappingTaxRate[];
  /**
   * Malta TIFD only. Present on the save request so postMapping can stringify
   * { mapping, activities }. Stripped from the POST body.
   */
  tifdAccountMappings?: TifdAccountMapping[];
  tifdActivities?: TifdActivity[];
  countryId?: string;
  typeChanged?: boolean;
};
